// Split from theme.js: product and collection page components (lines 2276-2303, 2614-2837, 3853-3960, 4698-6310, 7133-7263, 7352-7820, 7963-7993)
class BackInStockDrawer extends DrawerElement {
  constructor() {
    super()

    if (!theme.config.isTouch || Shopify.designMode) {
      this.init()
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  get submited() {
    return this.querySelector('.alert') !== null
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    // Open modal if errors or success message exist
    if (this.submited) {
      setTimeout(() => {
        this.show()
      }, 1000)
    }
  }
}
customElements.define('back-in-stock-drawer', BackInStockDrawer)
class RecentlyViewed extends HTMLElement {
  constructor() {
    super()

    if (!theme.config.hasLocalStorage) {
      this.hidden = true
      return
    }

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' })
  }

  init() {
    theme.utils
      .fetchWithRetry(this.getAttribute('data-url') + this.getQueryString(), {}, { label: 'recently-viewed' })
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser().parseFromString(responseText, 'text/html').querySelector('.shopify-section')

        const recommendations = sectionInnerHTML.querySelector('recently-viewed')
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML
          this.dispatchEvent(new CustomEvent('recentlyViewed:loaded'))
        } else {
          this.closest('.recently-section')?.remove()
          this.dispatchEvent(new CustomEvent('is-empty'))
        }
      })
      .catch((e) => {
        theme.utils.reportFetchError('recently-viewed', e)
      })
  }

  getQueryString() {
    let recentlyViewed = '[]'
    if (theme.config.hasLocalStorage) {
      recentlyViewed = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`) || '[]'
    }

    const items = new Set(JSON.parse(recentlyViewed))
    if (this.hasAttribute('data-product-id')) {
      items.delete(parseInt(this.getAttribute('data-product-id')))
    }
    return Array.from(items.values(), (item) => `id:${item}`)
      .slice(0, this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 4)
      .join(' OR ')
  }
}
customElements.define('recently-viewed', RecentlyViewed)

class ProductRecommendations extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' })
  }

  init() {
    theme.utils
      .fetchWithRetry(this.getAttribute('data-url'), {}, { label: 'product-recommendations' })
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser().parseFromString(responseText, 'text/html').querySelector('.shopify-section')

        if (sectionInnerHTML === null) return

        const recommendations = sectionInnerHTML.querySelector('product-recommendations')
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML
          this.dispatchEvent(new CustomEvent('recommendations:loaded'))
        } else {
          this.setAttribute('hidden', '')
          this.closest('.recommendations-section')?.remove()
          this.dispatchEvent(new CustomEvent('is-empty'))
        }
      })
      .catch((e) => {
        theme.utils.reportFetchError('product-recommendations', e)
      })
  }
}
customElements.define('product-recommendations', ProductRecommendations)

class ProductComplementary extends HTMLElement {
  constructor() {
    super()

    this.selectedIndex = this.selectedIndex
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString())
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }

  get container() {
    return this.closest('product-recommendations')
  }

  get prevButton() {
    return this.container.querySelector('[data-prev-button]')
  }

  get nextButton() {
    return this.container.querySelector('[data-next-button]')
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains('flickity')) {
        this.carousel = new theme.Carousel(
          this,
          {
            accessibility: false,
            prevNextButtons: false,
            adaptiveHeight: true,
            pageDots: false,
            contain: true,
            cellAlign: 'center',
            rightToLeft: theme.config.rtl
          },
          {
            previous: this.prevButton,
            next: this.nextButton
          }
        )

        this.carousel.load()
        this.carousel.slider.on('change', this.onChange.bind(this))
      }
    } else {
      this.container.hidden = true
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload()
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]'

      const fromElement = this.items[parseInt(oldValue)]
      const toElement = this.items[parseInt(newValue)]

      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1')
      })
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex')
      })
    }
  }

  onChange() {
    this.selectedIndex = this.carousel.slider.selectedIndex
  }
}
customElements.define('product-complementary', ProductComplementary)

class MediaElement extends HTMLElement {
  constructor() {
    super()
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false
  }

  get direction() {
    return this.hasAttribute('data-parallax-dir') ? this.getAttribute('data-parallax-dir') : 'vertical'
  }

  get media() {
    return Array.from(this.querySelectorAll('picture>img, video, iframe, svg, video-media, g-map'))
  }

  connectedCallback() {
    if (theme.config.motionReduced || !this.parallax) return
    this.setupParallax()
  }

  setupParallax() {
    const [scale, translate] = [1 + this.parallax, (this.parallax * 100) / (1 + this.parallax)]

    if (this.direction === 'vertical') {
      Motion.scroll(
        Motion.animate(
          this.media,
          { transform: [`scale(${scale}) translateY(0)`, `scale(${scale}) translateY(${translate}%)`], transformOrigin: ['bottom', 'bottom'] },
          { easing: 'linear' }
        ),
        { target: this, offset: ['start end', 'end start'] }
      )
    } else if (this.direction === 'horizontal') {
      Motion.scroll(
        Motion.animate(
          this.media,
          { transform: [`scale(${scale}) translateX(0)`, `scale(${scale}) translateX(${translate}%)`], transformOrigin: ['right', 'right'] },
          { easing: 'linear' }
        ),
        { target: this, offset: ['start end', 'end start'] }
      )
    } else {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(1)`, `scale(${scale})`], transformOrigin: ['center', 'center'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      )
    }
  }
}
customElements.define('media-element', MediaElement)
class SecondaryMedia extends HTMLElement {
  constructor() {
    super()

    Motion.inView(
      this,
      () => {
        setTimeout(() => this.init())
      },
      { margin: '200px 0px 200px 0px' }
    )
  }

  get template() {
    return this.previousElementSibling
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString())
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy()
  }

  init() {
    this.appendChild(this.template.content.cloneNode(true))
    this.mediaCount = this.querySelectorAll('.media').length
    this.items = Array.from(this.children)

    if (this.mediaCount > 1) {
      this.carousel = new Flickity(this, {
        accessibility: false,
        draggable: false,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl
      })

      if (this.mediaCount === 2) {
        this.classList.add('without-dots')
      }

      this.addEventListener('mousemove', this.onMoveHandler)
      this.addEventListener('mouseleave', this.onLeaveHandler)
      this.carousel.on('change', this.onChange.bind(this))
    }
  }

  onMoveHandler(event) {
    if (this.mediaCount === 2) {
      return this.carousel.select(1)
    }

    const { width } = this.carousel.size
    const mouseX = event.clientX - this.getBoundingClientRect().left

    if (this.mediaCount === 3) {
      if (mouseX < width / 2) {
        return this.carousel.select(1)
      }
      return this.carousel.select(2)
    }

    if (this.mediaCount === 4) {
      if (mouseX < width / 3) {
        return this.carousel.select(1)
      }
      if (mouseX < (2 * width) / 3) {
        return this.carousel.select(2)
      }
      return this.carousel.select(3)
    }
  }

  onLeaveHandler() {
    this.carousel.select(0)
  }

  onChange() {
    this.selectedIndex = this.carousel.selectedIndex
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)]
      const toElement = this.items[parseInt(newValue)]

      if (fromElement.tagName === 'VIDEO-MEDIA') {
        fromElement?.pause()
      }

      if (toElement.tagName === 'VIDEO-MEDIA') {
        toElement?.play()
      }
    }
  }
}
customElements.define('secondary-media', SecondaryMedia)

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve()
})
class DeferredMedia extends HTMLElement {
  constructor() {
    super()

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.onPosterClick.bind(this))
    }

    if (this.autoplay) {
      Motion.inView(this, this.init.bind(this))
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]')
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get autoplay() {
    return this.hasAttribute('autoplay')
  }

  get playing() {
    return this.hasAttribute('playing')
  }

  get player() {
    return (this.playerProxy =
      this.playerProxy ||
      new Proxy(this.playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target
            this.playerHandler(target, prop)
          }
        }
      }))
  }

  static get observedAttributes() {
    return ['playing']
  }

  onPosterClick(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.playing) {
      this.paused = true
      this.pause()
    } else {
      this.paused = false
      this.play()
    }
  }

  init() {
    if (!this.paused) {
      this.play()
    }
    return () => {
      this.pause()
    }
  }

  play() {
    if (!this.playing) {
      this.player.play()
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause()
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }))
      }

      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }))
      }
    }
  }
}

class VideoMedia extends DeferredMedia {
  constructor() {
    super()
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
      this.setAttribute('loaded', '')
      this.closest('.media')?.classList.remove('loading')

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template')
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true))
        }
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1023px)').matches
        const script = document.createElement('script')
        script.type = 'text/javascript'
        if (this.getAttribute('host') === 'youtube') {
          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api'
            document.head.appendChild(script)
            await new Promise((resolve2) => {
              script.onload = resolve2
            })
          }
          await onYouTubePromise
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute()
                }
                resolve(player)
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute('playing', '')
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute('playing')
                }
              }
            }
          })
        }
        if (this.getAttribute('host') === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js'
            document.head.appendChild(script)
            await new Promise((resolve2) => {
              script.onload = resolve2
            })
          }
          const player = new Vimeo.Player(this.querySelector('iframe'))
          if (muteVideo) {
            player.setMuted(true)
          }
          player.on('play', () => {
            this.setAttribute('playing', '')
          })
          player.on('pause', () => this.removeAttribute('playing'))
          player.on('ended', () => this.removeAttribute('playing'))
          resolve(player)
        }
      })
    } else {
      const templateElement = this.querySelector('template')
      if (templateElement) {
        templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true))
      }
      this.setAttribute('loaded', '')
      this.closest('.media')?.classList.remove('loading')

      const player = this.querySelector('video')
      player.addEventListener('play', () => {
        this.setAttribute('playing', '')
        this.removeAttribute('suspended')
      })
      player.addEventListener('pause', () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute('playing')
        }
      })
      return player
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute('host') === 'youtube') {
      prop === 'play' ? target.playVideo() : target.pauseVideo()
    } else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '')
            target.controls = true
            const replacementImageSrc = target.previousElementSibling?.currentSrc
            if (replacementImageSrc) {
              target.poster = replacementImageSrc
            }
          }
        })
      } else {
        target[prop]()
      }
    }
  }
}
customElements.define('video-media', VideoMedia)

class ModelMedia extends DeferredMedia {
  constructor() {
    super()

    this.player

    if (this.closeElement) {
      this.closeElement.addEventListener('click', this.onCloseClick.bind(this))
    }
  }

  get closeElement() {
    return this.querySelector('[id^="DeferredPosterClose-"]')
  }

  onCloseClick(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.modelViewerUI) {
      this.modelViewerUI.pause()
    }
  }

  playerTarget() {
    return new Promise(() => {
      this.setAttribute('loaded', '')

      Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this)
        },
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this)
        }
      ])
    })
  }

  playerHandler(target, prop) {
    target[prop]()
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', this.setupShopifyXR.bind(this))
      return
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent))
      modelJSON.remove()
    })
    window.ShopifyXR.setupXRElements()
  }

  setupModelViewerUI(errors) {
    if (errors) return

    const modelViewer = this.querySelector('model-viewer')
    if (modelViewer && !modelViewer.hasAttribute('loaded')) {
      modelViewer.setAttribute('loaded', '')
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this))
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this))

      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer)
    }
  }

  modelViewerPlayed() {
    this.setAttribute('playing', '')
    this.closeElement.removeAttribute('hidden')
    ;(this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', { bubbles: true }))
  }

  modelViewerPaused() {
    this.removeAttribute('playing')
    this.closeElement.setAttribute('hidden', '')
    ;(this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', { bubbles: true }))
  }
}
customElements.define('model-media', ModelMedia)

class VariantPicker extends HTMLElement {
  constructor() {
    super()
    this.currentVariant = null
    this.cachedVariantData = null
    this.currentOptions = []
    this.handleVariantChange = this.handleVariantChange.bind(this)
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map((el) => el.getAttribute('data-option-value-id'))
  }

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange)
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange)
  }

  handleVariantChange(event) {
    const target = event.target

    if (this.hasAttribute('data-dynamic-variants')) {
      this.syncCurrentOptions(target)
      this.findAndSetCurrentVariant(target)
      this.updateAvailableOptions(target)
    }

    this.markSelectedOption(target)
    this.publishSelectionChange(event, this.getActualInput(target))
  }

  markSelectedOption(target) {
    if (target.tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((opt) => opt.hasAttribute('selected'))
        ?.removeAttribute('selected')
      target.selectedOptions[0].setAttribute('selected', '')
    }
  }

  getActualInput(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
  }

  getVariantData() {
    if (this.cachedVariantData) return this.cachedVariantData

    const data = this.querySelector('[data-variants]')?.textContent
    return (this.cachedVariantData = data ? JSON.parse(data) : [])
  }

  findAndSetCurrentVariant(target) {
    this.currentVariant = this.findVariantByOptions(true) || this.findClosestAvailableVariant(target) || this.findVariantByOptions(false) || null
  }

  findVariantByOptions(requireAvailability) {
    return this.getVariantData().find((variant) => {
      const matches = this.currentOptions.every((val, i) => variant[`option${i + 1}`] === val)
      return requireAvailability ? matches && variant.available : matches
    })
  }

  findClosestAvailableVariant(lastSelectedOption) {
    if (!lastSelectedOption) return null

    const lastIndex = parseInt(lastSelectedOption.getAttribute('data-option-index'))
    const matches = this.getVariantData().filter(
      (variant) => variant.available && this.currentOptions.slice(0, lastIndex).every((val, i) => variant[`option${i + 1}`] === val)
    )

    return matches.reduce((best, variant) => {
      if (!best) return variant
      const bestScore = this.currentOptions.reduce((sum, val, i) => (best[`option${i + 1}`] === val ? sum + (3 - i) : sum), 0)
      const currentScore = this.currentOptions.reduce((sum, val, i) => (variant[`option${i + 1}`] === val ? sum + (3 - i) : sum), 0)
      return currentScore > bestScore ? variant : best
    }, null)
  }

  syncCurrentOptions(target) {
    this.currentOptions = Array.from(this.querySelectorAll('select, fieldset')).map((el) => {
      if (el.tagName === 'SELECT') return el.value
      return Array.from(el.querySelectorAll('input')).find((radio) => radio === target || radio.checked)?.value
    })
  }

  updateAvailableOptions(lastSelectedOption) {
    const firstValue = this.querySelector(':checked')?.value
    if (!firstValue) return

    const matchingVariants = this.getVariantData().filter((v) => v.option1 === firstValue)
    const groups = [...this.querySelectorAll('fieldset, .variant-wrapper')]

    groups.forEach((group, i) => {
      if (i === 0 || lastSelectedOption.parentElement === group) return

      const inputs = [...group.querySelectorAll('input[type="radio"], option')]
      const prevValue = groups[i - 1].querySelector(':checked')?.value

      const available = matchingVariants.filter((v) => v.available && v[`option${i}`] === prevValue).map((v) => v[`option${i + 1}`])

      this.setInputStates(inputs, available)
    })
  }

  setInputStates(inputs, availableValues) {
    inputs.forEach((input) => {
      const index = input.getAttribute('data-option-index')
      const value = input.getAttribute('value')
      const isAvailable = availableValues.includes(value)
      const isCurrent = this.currentVariant?.[`option${index}`] === value

      if (input.tagName === 'INPUT') {
        input.classList.toggle('disabled', !isAvailable)
        if (isCurrent) input.checked = true
      } else {
        isCurrent ? input.setAttribute('selected', '') : input.removeAttribute('selected')
      }
    })
  }

  publishSelectionChange(event, target) {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event, target, selectedOptionValues: this.selectedOptionValues }
    })
  }
}
customElements.define('variant-picker', VariantPicker)

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined
  cartUpdateUnsubscriber = undefined
  abortController = undefined
  pendingRequestUrl = null
  preProcessHtmlCallbacks = []
  postProcessHtmlCallbacks = []

  constructor() {
    super()
  }

  get sectionId() {
    return this.hasAttribute('data-original-section-id') ? this.getAttribute('data-original-section-id') : this.getAttribute('data-section-id')
  }

  get productId() {
    return this.getAttribute('data-product-id')
  }

  get productForm() {
    return document.forms[this.getAttribute('form')]
  }

  get productStickyForm() {
    return document.getElementById(`ProductStickyForm-${this.sectionId}-${this.productId}`)
  }

  get pickupAvailability() {
    return this.querySelector('pickup-availability')
  }

  get variantSelectors() {
    return this.querySelector('variant-picker')
  }

  get quantityInput() {
    return this.querySelector('quantity-input input')
  }

  connectedCallback() {
    this.initProductAnimation()

    this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    )

    this.initQuantityHandlers()
    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }))
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber()
    this.cartUpdateUnsubscriber?.()
  }

  initProductAnimation() {
    if (theme.config.motionReduced) return

    this.animation = new theme.Animation(this)
    this.animation.beforeLoad()

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media)
      this.animation.load()
    })
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    this.resetProductFormState()

    const productUrl = target.getAttribute('data-product-url') || this.pendingRequestUrl || this.getAttribute('data-product-url')
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.getAttribute('data-product-url') !== productUrl

    if (shouldSwapProduct) {
      window.location.href = productUrl
      return
    }

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
      targetId: target.tagName === 'OPTION' ? target.parentElement.id : target.id,
      callback: this.handleUpdateProductInfo()
    })
  }

  resetProductFormState() {
    this.productForm?.handleErrorMessage()
    this.productForm?.toggleSubmitButton(true, '')
    this.productStickyForm?.handleErrorMessage()
    this.productStickyForm?.toggleSubmitButton(true, '')
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    theme.utils
      .fetchWithRetry(requestUrl, { signal: this.abortController.signal }, { label: 'product-info' })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null
        if (callback !== undefined) {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html')
          callback(parsedHTML)
        }
      })
      .then(() => {
        // set focus to last clicked option value
        const activeElement = document.getElementById(targetId)
        if (activeElement === null) return

        if (activeElement.hasAttribute('align-selected')) {
          const scrollElement = activeElement.closest(activeElement.getAttribute('align-selected'))
          scrollElement?.scrollTo({
            left: activeElement.offsetLeft,
            behavior: 'instant'
          })
        }
        setTimeout(() => {
          activeElement.focus({ preventScroll: true })
        }, 100)
      })
      .catch((error) => {
        theme.utils.reportFetchError('product-info', error)
      })
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = []
    params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  handleUpdateProductInfo() {
    return (parsedHTML) => {
      const variant = this.getSelectedVariant(parsedHTML)

      this.pickupAvailability?.update(variant)
      this.updateOptionValues(parsedHTML)
      this.updateURL(variant?.id)
      this.updateVariantInputs(variant?.id)

      if (!variant) {
        this.setUnavailable()
        return
      }

      this.updateSourceFromDestination(parsedHTML, 'ProductGallery')
      this.updateSourceFromDestination(parsedHTML, 'Price')
      this.updateSourceFromDestination(parsedHTML, 'BuyButtonPrice')
      this.updateSourceFromDestination(parsedHTML, 'StickyPrice')
      this.updateSourceFromDestination(parsedHTML, 'Sku')
      this.updateSourceFromDestination(parsedHTML, 'Badges')
      this.updateSourceFromDestination(parsedHTML, 'Inventory')
      this.updateSourceFromDestination(parsedHTML, 'Volume')
      this.updateSourceFromDestination(parsedHTML, 'PricePerItem')
      this.updateSourceFromDestination(parsedHTML, 'BackInStock')
      this.updateSourceFromDestination(parsedHTML, 'ProductBundle')

      this.updateQuantityRules(parsedHTML)
      this.updateSourceFromDestination(parsedHTML, 'QuantityRules')
      this.updateSourceFromDestination(parsedHTML, 'QuantityRulesCart')
      this.updateSourceFromDestination(parsedHTML, 'VolumeNote')

      this.productForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut)
      this.productStickyForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut)

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          productId: this.productId,
          parsedHTML: parsedHTML,
          variant: variant
        }
      })
      ;(this.productForm ?? document).dispatchEvent(
        new CustomEvent('variant:change', {
          detail: {
            variant: variant
          }
        })
      )
    }
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.textContent
    return !!selectedVariant ? JSON.parse(selectedVariant) : null
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this.sectionId}-${this.productId}`)
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks)
    }
  }

  updateURL(variantId) {
    if (!variantId || this.getAttribute('data-update-url') === 'false') return

    const newUrl = new URL(window.location.href)
    newUrl.searchParams.set('variant', variantId)
    window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString())
  }

  updateVariantInputs(variantId) {
    if (!variantId) return

    const productForms = document.querySelectorAll(
      `#ProductForm-${this.sectionId}-${this.productId}, #ProductFormInstallment-${this.sectionId}-${this.productId}`
    )
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = variantId ?? ''
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this.sectionId}-${this.productId}`)
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`)
    if (source && destination) {
      destination.innerHTML = source.innerHTML
      destination.removeAttribute('hidden')
    }
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true)
    this.productStickyForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true)

    const selectors = [
      'Price',
      'BuyButtonPrice',
      'StickyPrice',
      'Inventory',
      'Sku',
      'Badges',
      'PricePerItem',
      'BackInStock',
      'ProductBundle',
      'VolumeNote',
      'Volume',
      'QuantityRules',
      'QuantityRulesCart'
    ]
      .map((id) => `#${id}-${this.sectionId}-${this.productId}`)
      .join(', ')
    document.querySelectorAll(selectors).forEach((selector) => selector.setAttribute('hidden', ''))
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return

    this.setQuantityBoundries()
    if (!this.hasAttribute('data-original-section-id')) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this))
    }
  }

  setQuantityBoundries() {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId
      }
    })
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value
    if (!currentVariantId) return

    this.querySelector('label[is="quantity-label"]')?.setAttribute('aria-busy', 'true')
    theme.utils
      .fetchWithRetry(`${this.getAttribute('data-product-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`, {}, { label: 'quantity-rules' })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html')
        this.updateQuantityRules(parsedHTML)
      })
      .catch((error) => {
        theme.utils.reportFetchError('quantity-rules', error)
      })
      .finally(() => {
        this.querySelector('label[is="quantity-label"]')?.removeAttribute('aria-busy')
      })
  }

  updateQuantityRules(parsedHTML) {
    if (!this.quantityInput) return

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityRules, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId,
        parsedHTML
      }
    })

    this.setQuantityBoundries()
  }
}
customElements.define('product-info', ProductInfo)

class ProductForm extends HTMLFormElement {
  constructor() {
    super()

    this.addEventListener('submit', this.onSubmitHandler)
  }

  connectedCallback() {
    const variantIdInput = this.variantIdInput
    if (!variantIdInput) return

    variantIdInput.disabled = false
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer')
  }

  get submitButton() {
    return (this._submitButton = this._submitButton || this.querySelector('[type="submit"]'))
  }

  get submitButtons() {
    return (this._submitButtons = this._submitButtons || document.querySelectorAll(`[type="submit"][form="${this.getAttribute('id')}"]`))
  }

  get hideErrors() {
    return this.getAttribute('data-hide-errors') === 'true'
  }

  get variantIdInput() {
    return this.querySelector('[name="id"]')
  }

  get bundles() {
    return Array.from(document.querySelectorAll(`[form="${this.getAttribute('id')}"] input[name="bundles"]:checked`))
  }

  prepareFormData(formData) {
    const bundlesLength = this.bundles.length
    const itemsArray = new Array(bundlesLength + 1)

    for (let i = 0; i < bundlesLength; i++) {
      const reverseIndex = bundlesLength - 1 - i
      itemsArray[i] = {
        id: this.bundles[reverseIndex].value,
        quantity: 1
      }
    }

    const allFormData = { items: itemsArray.slice(0, bundlesLength) }

    const json = {}
    const formEntries = Array.from(formData.entries())

    for (const [name, value] of formEntries) {
      if (name === 'id' || name === 'quantity' || name.includes('properties')) {
        if (name === 'id' || name === 'quantity') {
          json[name] = value
        } else {
          const regex = /(?:^(properties\[))(.*?)(?:\])/
          const property = regex.exec(name)[2]
          json.properties = json.properties || {}
          json.properties[property] = value
        }
      } else {
        allFormData[name] = value
      }
    }

    allFormData.items.push(json)
    return allFormData
  }

  onSubmitHandler(event) {
    const hasBundles = this.bundles.length > 0

    const data_obj = this.querySelector('[data-product-json]')
    if (data_obj) {
      const data = JSON.parse(data_obj.innerHTML)
      const { variants = [] } = data
      const selectedElements = [...this.querySelectorAll('input[data-option-index]:checked, select[data-option-index]')].reduce((acc, el) => {
        acc[el.dataset.optionIndex] = el.value
        return acc
      }, {})
      let selected_variant = false
      let array = variants
      for (const option in selectedElements) {
        const filtered = array.filter((variant) => {
          return variant[option] === selectedElements[option]
        })
        array = filtered
      }
      if (array[0]) selected_variant = array[0]
      if (selected_variant) {
        let add_to_cart_input = this.querySelector('[data-add-to-cart-id]')
        add_to_cart_input.value = selected_variant.id
      }
    }

    if (!Shopify.designMode) {
      if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
        if (hasBundles) {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...this.prepareFormData(new FormData(this))
            }
          })
          event.preventDefault()
        }
        return
      }
    }

    event.preventDefault()
    if (this.submitButton.hasAttribute('aria-disabled')) return
    this.activeElement = event.submitter || event.currentTarget

    this.handleErrorMessage()

    let sectionsToBundle = []
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }))

    const config = theme.utils.fetchConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']

    const formData = new FormData(this)
    formData.append('sections', sectionsToBundle)
    formData.append('sections_url', window.location.pathname)

    config.body = formData

    if (hasBundles) {
      const allFormData = this.prepareFormData(formData)
      config.body = JSON.stringify(allFormData)
      config.headers['Content-Type'] = 'application/json'
    }

    this.submitButton.setAttribute('aria-disabled', 'true')
    this.submitButton.setAttribute('aria-busy', 'true')

    theme.utils
      .queueCartWrite(() => theme.utils.fetchWithRetry(theme.routes.cart_add_url, config, { label: 'cart:add' }))
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          })
          this.handleErrorMessage(parsedState.description)
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: {
                errorMessage: parsedState.description
              }
            })
          )

          const submitButtonText = this.submitButton.querySelector('.btn-text>span')
          if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
            submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out')
            this.submitButton.setAttribute('aria-disabled', 'true')
            this.error = true
          }

          await this.refreshCart(formData.get('id'))

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }))
          return
        }

        if (Shopify.designMode) {
          if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
            window.location.href = theme.routes.cart_url
            return
          }
        }

        const refreshed = await this.refreshCart(formData.get('id'), parsedState)

        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: {
              product: parsedState
            }
          })
        )

        if (!refreshed) {
          if (!theme.utils.isUnloading()) window.location.href = theme.routes.cart_url
          return
        }

        this.cartDrawer?.show(this.activeElement)
      })
      .catch((error) => {
        theme.utils.reportFetchError('cart:add', error)
        if (!theme.utils.isBenignFetchError(error)) this.handleErrorMessage(theme.cartStrings.error)
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy')
        this.submitButtons.forEach((submitButton) => submitButton.removeAttribute('aria-busy'))

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled')
          this.submitButtons.forEach((submitButton) => submitButton.removeAttribute('aria-disabled'))
        }
      })
  }

  async refreshCart(productVariantId, parsedState = null) {
    try {
      const response = await theme.utils.fetchWithRetry(
        theme.routes.cart_url,
        { ...theme.utils.fetchConfig('json', 'GET') },
        { label: 'cart:refresh-after-add' }
      )
      const cartJson = await response.json()
      if (parsedState) cartJson['sections'] = parsedState['sections']

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId,
        cart: cartJson
      })
      return true
    }
    catch (error) {
      theme.utils.reportFetchError('cart:refresh-after-add', error)
      return false
    }
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message')
    if (!this.errorMessage) return

    this.errorMessage.toggleAttribute('hidden', !errorMessage)
    this.errorMessage.innerText = errorMessage
  }

  toggleSubmitButton(disable = true, text, unavailable = false) {
    if (!this.submitButton) return

    this.submitButton.removeAttribute('loading')
    this.submitButton.removeAttribute('unavailable')

    const submitButtonText = this.submitButton.querySelector('.btn-text')
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span')

    if (disable) {
      this.submitButton.setAttribute('disabled', '')
      if (unavailable) this.submitButton.setAttribute('unavailable', '')
      if (text) {
        ;(submitButtonTextChild || submitButtonText).textContent = text
      } else {
        this.submitButton.setAttribute('loading', '')
      }
    } else {
      this.submitButton.removeAttribute('disabled')
      ;(submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order')
        ? theme.variantStrings.preOrder
        : theme.variantStrings.addToCart
    }
  }
}
customElements.define('product-form', ProductForm, { extends: 'form' })

class ProductStickyForm extends HTMLElement {
  constructor() {
    super()

    this.scopeFrom = document.querySelector('.quick-order-list') || document.getElementById(this.getAttribute('form'))
    this.scopeTo = document.querySelector('.footer-group')

    if (!this.scopeFrom || !this.scopeTo) {
      return
    }

    const intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this))
    intersectionObserver.observe(this.scopeFrom)
    intersectionObserver.observe(this.scopeTo)

    this.onVariantChangedListener = this.onVariantChanged.bind(this)
  }

  get productForm() {
    return document.forms[this.getAttribute('form')]
  }

  get submitButton() {
    return (this._submitButton = this._submitButton || this.querySelector('[type="submit"]'))
  }

  get productOptions() {
    return (this._productOptions = this._productOptions || this.querySelector('[data-sticky-product-options]'))
  }

  get productMedia() {
    return (this._productMedia = this._productMedia || this.querySelector('[data-sticky-product-media]'))
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting
      }
    })

    if (this.scopeFromPassed && !this.scopeToReached) {
      Motion.animate(
        this.firstElementChild,
        { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] },
        { duration: 1, easing: [0.16, 1, 0.3, 1] }
      )
    } else {
      Motion.animate(
        this.firstElementChild,
        { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] },
        { duration: 1, easing: [0.16, 1, 0.3, 1] }
      )
    }
  }

  connectedCallback() {
    ;(this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener)
  }

  disconnectedCallback() {
    ;(this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener)
  }

  onVariantChanged(event) {
    this.updateProductMedia(event.detail.variant)
    this.updateProductOptions(event.detail.variant)
  }

  updateProductMedia(currentVariant) {
    if (!currentVariant || !currentVariant.featured_media) return

    const image = this.productMedia.querySelector('img')
    const newImage = new Image(currentVariant.featured_media.preview_image.width, currentVariant.featured_media.preview_image.height)

    newImage.alt = currentVariant.featured_media.alt
    newImage.src = currentVariant.featured_media.preview_image.src
    newImage.srcset = this.generateSrcset(currentVariant.featured_media.preview_image)
    newImage.sizes = image.sizes

    this.productMedia.replaceChildren(newImage)
  }

  updateProductOptions(currentVariant) {
    if (!currentVariant) return

    this.productOptions.innerText = currentVariant.title
  }

  handleErrorMessage(errorMessage = false) {
    if (this.productForm?.hideErrors) return

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message')
    if (!this.errorMessage) return

    this.errorMessage.toggleAttribute('hidden', !errorMessage)
    this.errorMessage.innerText = errorMessage
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return

    const submitButtonText = this.submitButton.querySelector('.btn-text')
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span')

    if (disable) {
      this.submitButton.setAttribute('disabled', '')
      if (text) {
        ;(submitButtonTextChild || submitButtonText).textContent = text
      }
    } else {
      this.submitButton.removeAttribute('disabled')
      ;(submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order')
        ? theme.variantStrings.preOrder
        : theme.variantStrings.addToCart
    }
  }

  generateSrcset(image) {
    const widths = this.productMedia
      .getAttribute('data-widths')
      .split(',')
      .map((width) => parseInt(width))
    return widths
      .filter((width) => width <= image.width)
      .map((width) => {
        return `${image.src}&width=${width} ${width}w`
      })
      .join(', ')
  }
}
customElements.define('product-sticky-form', ProductStickyForm)

class ProductBundleDetails extends AccordionDetails {
  constructor() {
    super()
  }

  cartUpdateUnsubscriber = undefined

  get productForm() {
    return document.forms[this.getAttribute('form')]
  }

  get bundles() {
    return Array.from(this.querySelectorAll('input[name="bundles"]:checked'))
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this))
    this.onBundleChangeListener = this.onBundleChange.bind(this)
    this.addEventListener('change', this.onBundleChangeListener)
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber()
    }
    this.removeEventListener('change', this.onBundleChangeListener)
  }

  onCartUpdate(event) {
    if (this.bundles.length === 0) return

    if (event.source === 'product-form') {
      this.bundles.forEach((input) => {
        if (!input.disabled) input.checked = false
      })
      this.onBundleChange()
    }
  }

  onBundleChange() {
    ;(this.productForm ?? document).dispatchEvent(
      new CustomEvent('bundle:change', {
        detail: {
          items: this.bundles.map((input) => ({
            id: input.value,
            quantity: 1,
            price: input.getAttribute('data-price')
          }))
        }
      })
    )
  }
}
customElements.define('product-bundle-details', ProductBundleDetails, { extends: 'details' })

class ProductBundlePicker extends HTMLLabelElement {
  constructor() {
    super()
    this.handleVariantChange = this.handleVariantChange.bind(this)
  }

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange)
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange)
  }

  handleVariantChange(event) {
    const target = event.target

    if (target.tagName !== 'SELECT') return

    const option = target.selectedOptions?.[0]
    if (!option) return

    const value = option.value
    const price = option.getAttribute('data-price')
    const imgSrc = option.getAttribute('data-img-src')
    const imgSrcset = option.getAttribute('data-img-srcset')

    this.updateCheckboxInput(value, price)
    this.updatePrice(price)
    this.updateImage(imgSrc, imgSrcset)
  }

  updateCheckboxInput(value, price) {
    const input = this.querySelector('input[type="checkbox"]')
    if (!input) return

    input.value = value
    input.setAttribute('data-price', price)
  }

  updatePrice(price) {
    const priceElement = this.querySelector('.price__regular')
    if (!priceElement || !price) return

    const format = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat

    priceElement.innerHTML = theme.Currency.formatMoney(price, format)
  }

  updateImage(src, srcset) {
    const img = this.querySelector('img')
    if (!img) return

    if (src) img.src = src
    if (srcset) img.srcset = srcset
  }
}
customElements.define('product-bundle-picker', ProductBundlePicker, { extends: 'label' })

class ProductBuyPrice extends HTMLElement {
  constructor() {
    super()
  }

  get productForm() {
    return document.forms[this.getAttribute('form')]
  }

  connectedCallback() {
    this.onBundleChangedListener = this.onBundleChanged.bind(this)
    ;(this.productForm ?? document).addEventListener('bundle:change', this.onBundleChangedListener)
  }

  disconnectedCallback() {
    ;(this.productForm ?? document).removeEventListener('bundle:change', this.onBundleChangedListener)
  }

  onBundleChanged(event) {
    const { items } = event.detail
    let subtotal = parseInt(this.getAttribute('data-price'))

    items.forEach(({ price, quantity }) => {
      subtotal += parseInt(price) * parseInt(quantity)
    })

    this.innerHTML = theme.Currency.formatMoney(
      subtotal,
      theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat
    )
  }
}
customElements.define('product-buy-price', ProductBuyPrice)

class MediaGallery extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, () => {
      requestAnimationFrame(() => this.pauseAllMedia())
      //setTimeout(() => this.pauseAllMedia(), 500);
    })
    ;(this.productForm ?? document).addEventListener('variant:change', this.onVariantChanged.bind(this))

    this.addEventListener('lightbox:open', (event) => this.openZoom(event.detail.index))
    this.sliderGallery.addEventListener('slider:change', this.onSlideChange.bind(this))

    this.countMediaGallery()
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]')
  }

  get productForm() {
    return document.forms[this.getAttribute('form')]
  }

  get sliderGallery() {
    return this.querySelector('slider-element')
  }

  get mediaPreview() {
    return this.querySelector('.product__preview .product__media')
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),

      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,

      // Recommended options for this plugin
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false
    })

    lightbox.addFilter('thumbEl', (_thumbEl, data) => data['thumbnailElement'])

    lightbox.on('contentLoad', (event) => {
      const { content } = event

      if (content.type === 'video' || content.type === 'external_video' || content.type === 'model') {
        event.preventDefault()

        // Create a container for video and assign it to the `content.element` property
        content.element = document.createElement('div')
        content.element.className = 'pswp__video-container'
        content.element.appendChild(content.data.domElement.cloneNode(true))
      }
    })

    lightbox.on('change', () => {
      this.sliderGallery.select(lightbox.pswp.currIndex + 1, true)
    })

    lightbox.on('close', () => {
      const slideSelected = this.sliderGallery.selected(lightbox.pswp.currIndex)
      lightbox.pswp._lastActiveElement = slideSelected ? slideSelected.querySelector('button') : document.activeElement
    })

    lightbox.init()
    return (this._photoswipe = lightbox)
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant
    if (!currentVariant.featured_media) return

    this.countMediaGallery()
    this.setActiveMedia(currentVariant.featured_media.id)
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement
    this.playActiveMedia(activeMedia)

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'))
      } else {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'))
      }
    }
  }

  setActiveMedia(mediaId) {
    let activeMedia = this.sliderGallery.querySelector(`[data-media-id="${mediaId}"]`)
    if (activeMedia?.checkVisibility() === false) {
      activeMedia = activeMedia.nextElementSibling
    }
    if (activeMedia === null) return

    activeMedia.scrollIntoView({
      block: 'start',
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    })
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === 'undefined') return

    const deferredMedia = activeMedia.querySelector('.deferred-media')

    this.sliderGallery.querySelectorAll('.deferred-media').forEach((media) => {
      deferredMedia === media ? media.play() : media.pause()
    })
  }

  pauseAllMedia() {
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      if (index > 0) {
        const deferredMedia = media.querySelector('.deferred-media')
        if (deferredMedia && typeof deferredMedia.pause === 'function') deferredMedia.pause()
      }
    })
  }

  openZoom(index = 0) {
    let dataSource = this.sliderGallery.itemsToShow.map((media) => {
      const image = media.querySelector('img')

      if (media.getAttribute('data-media-type') === 'image') {
        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        }
      }

      if (
        media.getAttribute('data-media-type') === 'video' ||
        media.getAttribute('data-media-type') === 'external_video' ||
        media.getAttribute('data-media-type') === 'model'
      ) {
        const video = media.querySelector('.deferred-media')
        return {
          thumbnailElement: image,
          domElement: video,
          type: media.getAttribute('data-media-type'),
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: 800,
          height: 800 / video.getAttribute('data-aspect-ratio'),
          alt: image.alt,
          thumbCropped: true
        }
      }
    })

    if (this.mediaPreview && this.mediaPreview.offsetParent) {
      if (this.mediaPreview.getAttribute('data-media-type') === 'image') {
        const image = this.mediaPreview.querySelector('img')
        dataSource.push({
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        })

        if (index === -1) {
          index = dataSource.length - 1
        }
      }
    }

    this.photoswipe.loadAndOpen(index, dataSource)
  }

  countMediaGallery() {
    let mediaCount = 0
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => {
      if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
        mediaCount++
      }
    })

    if (this.mediaPreview) {
      mediaCount++
    }

    mediaCount > 1 ? this.classList.remove('with-only1') : this.classList.add('with-only1')
  }
}
customElements.define('media-gallery', MediaGallery)

class MediaLightboxButton extends HTMLButtonElement {
  constructor() {
    super()

    this.addEventListener('click', this.onButtonClick)
  }

  onButtonClick() {
    const media = this.closest('[data-media-id]')
    const sliderGallery = this.closest('slider-element')
    const openIndex = sliderGallery ? sliderGallery.itemsToShow.indexOf(media) : -1

    this.dispatchEvent(
      new CustomEvent('lightbox:open', {
        bubbles: true,
        detail: {
          index: openIndex
        }
      })
    )
  }
}
customElements.define('media-lightbox-button', MediaLightboxButton, { extends: 'button' })

class MediaHoverButton extends MediaLightboxButton {
  constructor() {
    super()
  }

  get zoomRatio() {
    return 2
  }

  onButtonClick(event) {
    if (theme.config.isTouch) {
      super.onButtonClick()
    } else {
      const media = this.closest('[data-media-type="image"]')
      if (media) {
        const image = media.querySelector('img')
        this.gallery = this.closest('slider-element')

        this.magnify(image)
        this.moveWithHover(image, event)
      }
    }
  }

  createOverlay(image) {
    const overlayImage = document.createElement('img')
    overlayImage.setAttribute('src', `${image.src}`)
    const overlay = document.createElement('media-hover-overlay')
    this.prepareOverlay(overlay, overlayImage)

    this.toggleLoadingSpinner(image)

    overlayImage.onload = () => {
      this.toggleLoadingSpinner(image)
      image.parentElement.insertBefore(overlay, image)
    }

    if (this.gallery) this.gallery.classList.add('magnify')

    return overlay
  }

  prepareOverlay(container, image) {
    container.setAttribute('class', 'media z-10 absolute top-0 left-0 w-full h-full')
    container.setAttribute('aria-hidden', 'true')
    container.style.backgroundImage = `url('${image.src}')`
    container.style.cursor = 'zoom-out'
  }

  toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement
    loadingSpinner.classList.toggle('loading')
    loadingSpinner.classList.toggle('pointer-events-none')
  }

  moveWithHover(image, event) {
    // calculate mouse position
    const ratio = image.height / image.width
    const container = event.target.getBoundingClientRect()
    const xPosition = event.clientX - container.left
    const yPosition = event.clientY - container.top
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`
    this.overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`
  }

  magnify(image) {
    this.overlay = this.createOverlay(image)
    this.overlay.onclick = () => this.reset()
    this.overlay.onmousemove = (event) => this.moveWithHover(image, event)
    this.overlay.onmouseleave = () => this.reset()
  }

  reset() {
    this.overlay.remove()

    if (this.gallery) this.gallery.classList.remove('magnify')
  }
}
customElements.define('media-hover-button', MediaHoverButton, { extends: 'button' })

class MediaDots extends SliderDots {
  constructor() {
    super()
  }

  init() {
    super.init()
    this.resetIndexes()
  }

  resetIndexes() {
    let newIndex = 1
    this.itemsToShow.forEach((item, index) => {
      item.setAttribute('data-index', newIndex)
      item.setAttribute('aria-current', index === 0 ? 'true' : 'false')

      newIndex++
    })
  }
}
customElements.define('media-dots', MediaDots)
class QuickView extends XModal {
  constructor() {
    super()
  }

  get selector() {
    return '.quick-view__content'
  }

  cartUpdateUnsubscriber = undefined

  connectedCallback() {
    super.connectedCallback()

    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.hide.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber()
    }
  }

  beforeShow() {
    super.beforeShow()
    this.quickview()
  }

  afterShow() {
    super.afterShow()

    document.dispatchEvent(
      new CustomEvent('quickview:open', {
        detail: {
          productUrl: this.getAttribute('data-product-url')
        }
      })
    )
  }

  afterHide() {
    super.afterHide()

    const drawerContent = this.querySelector(this.selector)
    drawerContent.innerHTML = ''
  }

  quickview() {
    const drawerContent = this.querySelector(this.selector)
    const productUrl = this.getAttribute('data-product-url').split('?')[0]
    const sectionUrl = `${productUrl}?view=modal`

    theme.utils
      .fetchWithRetry(sectionUrl, {}, { label: 'quickview' })
      .then((response) => response.text())
      .then((responseText) => {
        setTimeout(() => {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html')
          const productElement = parsedHTML.querySelector(this.selector)
          this.setInnerHTML(drawerContent, productElement.innerHTML)
          theme.a11y.trapFocus(this, this.focusElement)

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init()
          }

          document.dispatchEvent(
            new CustomEvent('quickview:loaded', {
              detail: {
                productUrl: this.getAttribute('data-product-url')
              }
            })
          )
        }, 200)
      })
      .catch((e) => {
        theme.utils.reportFetchError('quickview', e)
      })
  }

  setInnerHTML(element, innerHTML) {
    element.innerHTML = innerHTML

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script')
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      })
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
    })
  }
}
customElements.define('quick-view', QuickView)

class FiltersToggle extends HoverButton {
  constructor() {
    super()

    this.addEventListener('click', this.onClick)

    if (theme.config.hasLocalStorage) {
      const expanded = window.localStorage.getItem(`${theme.settings.themeName}:filters-toggle`) || 'false'
      this.onToggle(expanded === 'true')
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true'
  }

  onClick() {
    this.onToggle(this.expanded)
  }

  onToggle(expanded) {
    this.setAttribute('aria-expanded', expanded ? 'false' : 'true')
    if (this.controlledElement) this.controlledElement.classList.toggle('xl:block', !expanded)

    if (theme.config.hasLocalStorage) {
      window.localStorage.setItem(`${theme.settings.themeName}:filters-toggle`, expanded)
    }
  }
}
customElements.define('filters-toggle', FiltersToggle, { extends: 'button' })
class ProductCardInfo extends ProductInfo {
  constructor() {
    super()

    this._sectionId = 'ProductCard-template'
  }

  get pickerType() {
    return this.getAttribute('data-picker-type')
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = []
    params.push('view=bundle-card')

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this._sectionId}-${this.productId}`)
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`)
    if (source && destination) {
      destination.innerHTML = source.innerHTML
      destination.removeAttribute('hidden')
    }
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this._sectionId}-${this.productId}-${this.pickerType}`)
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks)
    }
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(
      `product-card-info[data-product-id="${this.productId}"] [data-selected-variant]`
    )?.textContent
    return !!selectedVariant ? JSON.parse(selectedVariant) : null
  }
}
customElements.define('product-card-info', ProductCardInfo)

class ProductBundleForm extends HTMLFormElement {
  constructor() {
    super()

    this.querySelector('[name="id"]').disabled = false
    this.addEventListener('submit', this.onSubmitHandler)
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get submitButton() {
    return (this._submitButton = this._submitButton || this.querySelector('[type="submit"]'))
  }

  getSelectedVariant() {
    const selectedVariant = this.querySelector('[data-selected-variant]')?.textContent
    return !!selectedVariant ? JSON.parse(selectedVariant) : null
  }

  onSubmitHandler(event) {
    event.preventDefault()
    if (this.submitButton.hasAttribute('aria-disabled')) return

    const product = this.closest('.product-card')
    const variant = this.getSelectedVariant()
    variant.default_featured_media = this.hasAttribute('data-product-image') ? this.getAttribute('data-product-image') : ''

    this.submitButton.removeAttribute('aria-busy')
    ;(this.controlledElement ?? this).dispatchEvent(
      new CustomEvent('bundle:added', { bubbles: true, detail: { product: product, variant: variant } })
    )
  }

  handleErrorMessage() {
    // todo
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return

    const submitButtonText = this.submitButton.querySelector('.btn-text')
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span')

    if (disable) {
      this.submitButton.setAttribute('disabled', '')
      if (text) {
        ;(submitButtonTextChild || submitButtonText).textContent = text
      }
    } else {
      this.submitButton.removeAttribute('disabled')
      ;(submitButtonTextChild || submitButtonText).textContent = theme.variantStrings.addToBundle
    }
  }
}
customElements.define('product-bundle-form', ProductBundleForm, { extends: 'form' })

class ProductBundleRemoveButton extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('click', (event) => {
      const variant = event.target.closest('[data-product-bundle-variant]')
      ;(this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:removed', { bubbles: true, detail: { variant: variant } }))
    })
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }
}
customElements.define('product-bundle-remove-button', ProductBundleRemoveButton)

class ProductBundleToggleButton extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('click', () => {
      this.controlledElement.classList.toggle('active')
    })
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }
}
customElements.define('product-bundle-toggle-button', ProductBundleToggleButton)

class ProductBundle extends HTMLElement {
  constructor() {
    super()

    this.template
    this.bundleCount = 0

    this.addEventListener('bundle:added', this.onAddHandler.bind(this))
    this.addEventListener('bundle:removed', this.onRemoveHandler.bind(this))
    this.addEventListener('change', theme.utils.debounce(this.onChangeHandler.bind(this), 300))
    this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this))
  }

  get bundleMin() {
    return this.hasAttribute('data-minimum') ? parseInt(this.getAttribute('data-minimum')) : 3
  }

  get bundleMax() {
    const minimum = this.bundleMin
    const maximum = this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : 3
    return maximum > minimum ? maximum : minimum
  }

  get variants() {
    return Array.from(this.querySelectorAll('[data-product-bundle-variant]'))
  }

  get submitButton() {
    return this.querySelector('[data-product-bundle-submit]')
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer')
  }

  get preventDuplicate() {
    return this.hasAttribute('data-prevent-duplicate')
  }

  get template() {
    return (this._template = this._template || this.querySelector('[data-product-bundle-variant][available]').cloneNode(true))
  }

  onChangeHandler() {
    this.updateTotal()
    this.updateTotalWithCurrency()
    this.updateProgressBar()
  }

  onSubmitHandler(event) {
    const validateQuantity = this.variants.every((variant) => {
      const target = variant.querySelector('quantity-input').input
      const inputValue = parseInt(target.value)
      let message = ''

      if (inputValue < parseInt(target.getAttribute('data-min'))) {
        message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'))
      } else if (inputValue > parseInt(target.max)) {
        message = theme.quickOrderListStrings.maxError.replace('[max]', target.max)
      } else if (inputValue % parseInt(target.step) !== 0) {
        message = theme.quickOrderListStrings.stepError.replace('[step]', target.step)
      }

      if (message) {
        target.setCustomValidity(message)
        target.reportValidity()
        target.value = target.defaultValue
        target.select()

        return false
      }

      return true
    })
    if (!validateQuantity) return

    const data = {
      items: this.variants.map((variant) => ({
        id: variant.getAttribute('data-variant-id'),
        quantity: variant.querySelector('quantity-input').value || 1
      }))
    }

    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data
        }
      })
      return
    }

    event.preventDefault()
    if (this.submitButton.hasAttribute('aria-disabled')) return
    this.activeElement = event.submitter || event.currentTarget

    this.handleErrorMessage()

    let sectionsToBundle = []
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }))

    const body = JSON.stringify({
      ...data,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    })

    this.submitButton.setAttribute('aria-disabled', 'true')
    this.submitButton.setAttribute('aria-busy', 'true')

    theme.utils
      .queueCartWrite(() => theme.utils.fetchWithRetry(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body }, { label: 'cart:add-bundle' }))
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description)
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: {
                errorMessage: parsedState.description
              }
            })
          )

          const submitButtonText = this.submitButton.querySelector('.btn-text>span')
          if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
            submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out')
            this.submitButton.setAttribute('aria-disabled', 'true')
            this.error = true
          }

          await this.refreshCart()

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }))
          this.resetVariants()
          return
        }

        const refreshed = await this.refreshCart(parsedState)

        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: {
              product: parsedState
            }
          })
        )

        this.resetVariants()

        if (!refreshed) {
          if (!theme.utils.isUnloading()) window.location.href = theme.routes.cart_url
          return
        }

        this.cartDrawer?.show(this.activeElement)
      })
      .catch((error) => {
        theme.utils.reportFetchError('cart:add-bundle', error)
        if (!theme.utils.isBenignFetchError(error)) this.handleErrorMessage(theme.cartStrings.error)
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy')

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled')
        }
      })
  }

  async refreshCart(parsedState = null) {
    try {
      const response = await theme.utils.fetchWithRetry(
        theme.routes.cart_url,
        { ...theme.utils.fetchConfig('json', 'GET') },
        { label: 'cart:refresh-after-add-bundle' }
      )
      const cartJson = await response.json()
      if (parsedState) cartJson['sections'] = parsedState['sections']

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson })
      return true
    }
    catch (error) {
      theme.utils.reportFetchError('cart:refresh-after-add-bundle', error)
      return false
    }
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message')
    if (!this.errorMessage) return

    this.errorMessage.toggleAttribute('hidden', !errorMessage)
    this.errorMessage.innerText = errorMessage
  }

  onAddHandler(event) {
    this.addVariant(event.detail.product, event.detail.variant)
    this.updateTotal()
    this.updateTotalWithCurrency()
    this.updateProgressBar()
  }

  onRemoveHandler(event) {
    this.clearVariant(event.detail.variant)
    this.reorderVariants()
    this.updateTotal()
    this.updateTotalWithCurrency()
    this.updateProgressBar()
  }

  updateTotal() {
    let subtotal = 0
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]')
        const quantityInput = variant.querySelector('quantity-input')
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value)
      }
    })

    const element = this.querySelector('[data-product-bundle-total]')
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat)
  }

  updateTotalWithCurrency() {
    let subtotal = 0
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]')
        const quantityInput = variant.querySelector('quantity-input')
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value)
      }
    })

    const element = this.querySelector('[data-product-bundle-total-with-currency]')
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyWithCurrencyFormat)
  }

  updateProgressBar() {
    const percent = (100 * this.bundleCount) / this.bundleMin
    const element = this.querySelector('[data-product-bundle-progress-bar]')
    element.style.setProperty('--progress', `${percent}%`)
  }

  addVariant(product, variant) {
    this.bundleCount++

    if (this.bundleCount >= this.bundleMin) {
      this.submitButton.disabled = false
    }
    if (this.bundleCount >= this.bundleMax) {
      this.closest('.product-bundle-wrapper').setAttribute('locked', '')
    }

    let variantElement = this.getAvailableAvriant()
    if (variantElement === null) {
      variantElement = this.template.cloneNode(true)
      variantElement.style.order = this.bundleCount
      this.querySelector('.product-bundle__body').appendChild(variantElement)
    }

    if (variantElement) {
      const preview_image = variant.featured_media ? variant.featured_media.preview_image.src : variant.default_featured_media
      const media = variantElement.querySelector('[data-product-bundle-variant-media]')
      if (preview_image.length > 0) {
        media.innerHTML = `<img src="${this.getResizedImageSrc(preview_image, 180)}" srcset="${this.getResizedImageSrc(preview_image, 180)} 180w, ${this.getResizedImageSrc(preview_image, 360)} 360w, ${this.getResizedImageSrc(preview_image, 540)} 540w" loading="lazy" is="lazy-image" />`
      }

      const content = variantElement.querySelector('[data-product-bundle-variant-content]')
      content.innerHTML = `
        <div class="flex flex-col gap-1">
          <p class="horizontal-product__title font-medium text-base leading-tight">${product.querySelector('[data-product-bundle-title]').textContent}</p>
          ${
            variant.options.length > 0
              ? `
            <ul class="grid gap-1d5">
              ${variant.options
                .map((option) => {
                  if (option !== 'Default Title') {
                    return `<li class="text-xs text-opacity leading-tight">${option}</li>`
                  }
                })
                .join('')}
            </ul>
          `
              : ''
          }
        </div>
        <div class="price text-sm flex flex-wrap gap-1d5" data-price="${variant.price}" data-product-bundle-variant-price>
          ${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}
        </div>`

      variantElement.product = product
      variantElement.setAttribute('data-variant-id', variant.id)
      variantElement.removeAttribute('available')
      if (this.preventDuplicate) {
        product.setAttribute('locked', '')
      }
    }
  }

  clearVariant(variant) {
    if (this.preventDuplicate) {
      variant.product.removeAttribute('locked')
    }
    variant.setAttribute('available', '')
    variant.removeAttribute('data-variant-id')
    variant.querySelector('[data-product-bundle-variant-media]').innerHTML = ''
    variant.querySelector('[data-product-bundle-variant-media]').classList.remove('loading')
    variant.querySelector('[data-product-bundle-variant-content]').innerHTML =
      '<span class="horizontal-product__skeleton"></span><span class="horizontal-product__skeleton"></span>'
    variant.querySelector('quantity-input').reset()

    this.bundleCount--

    if (this.bundleCount < this.bundleMin) {
      this.submitButton.disabled = true
    } else {
      variant.remove()
    }
    if (this.bundleCount < this.bundleMax) {
      this.closest('.product-bundle-wrapper').removeAttribute('locked')
    }
  }

  reorderVariants() {
    let order = 0
    let minimum = Math.max(this.bundleMin, this.bundleCount) * -1
    this.variants.forEach((variant) => {
      variant.style.order = variant.hasAttribute('available') ? order++ : minimum++
    })
  }

  resetVariants() {
    this.variants.forEach((variant) => {
      this.clearVariant(variant)
    })

    this.reorderVariants()
    this.updateTotal()
    this.updateTotalWithCurrency()
    this.updateProgressBar()
  }

  getAvailableAvriant() {
    return this.querySelector('[data-product-bundle-variant][available]')
  }

  getResizedImageSrc(src, size) {
    return `${src}${src.includes('?') ? '&' : '?'}width=${size}&height=${size}`.replace(/\n|\r|\s/g, '')
  }
}
customElements.define('product-bundle', ProductBundle)
class SecondaryVideo extends HTMLDivElement {
  constructor() {
    super()

    if (theme.config.motionReduced || theme.config.isTouch) return

    if (this.videoMedia) {
      this.addEventListener('mouseenter', this.onEnterHandler)
      this.addEventListener('mouseleave', this.onLeaveHandler)
    }
  }

  get videoMedia() {
    return this.querySelector('video-media')
  }

  onEnterHandler() {
    const media = this.videoMedia
    if (media && media.hasAttribute('autoplay')) {
      media.play()
    }
  }

  onLeaveHandler() {
    const media = this.videoMedia
    if (media) {
      media.pause()
    }
  }
}
customElements.define('secondary-video', SecondaryVideo, { extends: 'div' })
